import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Plus, Trash2, MapPin, CalendarDays, Save, ClipboardList, Clock } from "lucide-react";
import { toast } from "sonner";

const REHEARSAL_TEMPLATES = {
  wedding: [
    "Walk the processional order",
    "Confirm ceremony seating layout with venue",
    "Practice ring exchange & vows cadence",
    "Cue music with officiant/DJ",
    "Recessional walk-through",
    "Distribute readings & assign readers",
    "Confirm rehearsal dinner logistics",
  ],
  corporate: [
    "AV / mic test",
    "Confirm speaker order & timing",
    "Walk speakers through the run of show",
    "Test slides & clicker",
    "Confirm room turnover between sessions",
  ],
  gala: [
    "Confirm program order with MC",
    "Auctioneer walk-through with lot list",
    "Cue lighting & sound for entrances",
    "Rehearse honorees' remarks & seating",
  ],
};

export default function RehearsalTab({ ev, canEdit, reload }) {
  const eventId = ev.id;
  const [tasks, setTasks] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [taskInput, setTaskInput] = useState("");
  const [rehearsalDate, setRehearsalDate] = useState(ev.rehearsal_date || "");
  const [rehearsalVenue, setRehearsalVenue] = useState(ev.rehearsal_venue || "");
  const [savingDate, setSavingDate] = useState(false);
  const [openBlock, setOpenBlock] = useState(false);
  const [blockForm, setBlockForm] = useState({ time: "16:00", title: "", description: "", duration_min: 15, location: "" });

  const load = async () => {
    try {
      const [t, b] = await Promise.all([
        api.get(`/events/${eventId}/rehearsal/tasks`),
        api.get(`/events/${eventId}/rehearsal/timeline`),
      ]);
      setTasks(t.data); setBlocks(b.data);
    } catch (e) { toast.error("Could not load rehearsal"); }
  };
  useEffect(() => { load(); }, [eventId]);

  const saveRehearsalDetails = async () => {
    setSavingDate(true);
    try {
      await api.patch(`/events/${eventId}`, { rehearsal_date: rehearsalDate || null, rehearsal_venue: rehearsalVenue || null });
      toast.success("Rehearsal details saved");
      reload && reload();
    } catch (e) { toast.error("Could not save"); }
    finally { setSavingDate(false); }
  };

  const addTask = async () => {
    if (!taskInput.trim()) return;
    try {
      const { data } = await api.post(`/events/${eventId}/rehearsal/tasks`, { title: taskInput.trim(), done: false });
      setTasks([...tasks, data]);
      setTaskInput("");
    } catch (e) { toast.error("Could not add"); }
  };

  const toggleTask = async (t) => {
    const done = !t.done;
    setTasks(tasks.map(x => x.id === t.id ? { ...x, done } : x));
    try { await api.patch(`/events/${eventId}/rehearsal/tasks/${t.id}`, { done }); }
    catch (e) { toast.error("Could not update"); }
  };

  const delTask = async (id) => {
    try { await api.delete(`/events/${eventId}/rehearsal/tasks/${id}`); setTasks(tasks.filter(t => t.id !== id)); }
    catch (e) { toast.error("Could not delete"); }
  };

  const loadTemplate = async () => {
    const t = (ev.event_type || "").toLowerCase();
    let preset;
    if (t.includes("wedding") || t.includes("elopement") || t.includes("vow")) preset = REHEARSAL_TEMPLATES.wedding;
    else if (t.includes("corporate") || t.includes("company") || t.includes("conference") || t.includes("seminar") || t.includes("training")) preset = REHEARSAL_TEMPLATES.corporate;
    else if (t.includes("gala") || t.includes("fundraiser") || t.includes("auction") || t.includes("benefit")) preset = REHEARSAL_TEMPLATES.gala;
    else preset = REHEARSAL_TEMPLATES.wedding;
    try {
      const created = await Promise.all(preset.map(title => api.post(`/events/${eventId}/rehearsal/tasks`, { title, done: false })));
      setTasks([...tasks, ...created.map(r => r.data)]);
      toast.success(`Added ${preset.length} rehearsal checklist items`);
    } catch (e) { toast.error("Could not load template"); }
  };

  const addBlock = async () => {
    if (!blockForm.title || !blockForm.time) { toast.error("Time and title required"); return; }
    try {
      const { data } = await api.post(`/events/${eventId}/rehearsal/timeline`, {
        ...blockForm, duration_min: Number(blockForm.duration_min) || 15,
      });
      setBlocks([...blocks, data].sort((a,b) => (a.time || "").localeCompare(b.time || "")));
      setOpenBlock(false);
      setBlockForm({ time: "16:00", title: "", description: "", duration_min: 15, location: "" });
      toast.success("Added to rehearsal timeline");
    } catch (e) { toast.error("Could not add"); }
  };

  const delBlock = async (id) => {
    if (!confirm("Delete this block?")) return;
    try { await api.delete(`/events/${eventId}/rehearsal/timeline/${id}`); setBlocks(blocks.filter(b => b.id !== id)); }
    catch (e) { toast.error("Could not delete"); }
  };

  const done = tasks.filter(t => t.done).length;

  return (
    <div className="space-y-10">
      {/* Rehearsal details */}
      <section className="bevel border-hair rounded-sm p-6 md:p-8">
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2">MOD.REHEARSAL / NIGHT_BEFORE</div>
        <h3 className="font-display text-xl md:text-2xl font-bold text-app tracking-tight leading-none mb-5">The night before</h3>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <F label="Rehearsal date">
            <Input
              type="date"
              data-testid="rehearsal-date-input"
              value={rehearsalDate}
              onChange={(e) => setRehearsalDate(e.target.value)}
              disabled={!canEdit}
              className="rounded-sm"
            />
          </F>
          <F label="Rehearsal venue">
            <Input
              data-testid="rehearsal-venue-input"
              value={rehearsalVenue}
              onChange={(e) => setRehearsalVenue(e.target.value)}
              placeholder="e.g. Ceremony site or nearby restaurant"
              disabled={!canEdit}
              className="rounded-sm"
            />
          </F>
          {canEdit && (
            <Button data-testid="save-rehearsal-details" onClick={saveRehearsalDetails} disabled={savingDate} className="rounded-sm bg-app hover:bg-[#000000] h-10">
              <Save size={14} strokeWidth={1.5} className="mr-2" /> Save
            </Button>
          )}
        </div>
        {rehearsalDate && (
          <div className="mt-4 flex items-center gap-4 text-sm text-muted font-light">
            <span className="flex items-center gap-1.5"><CalendarDays size={13} strokeWidth={1.5} />{new Date(rehearsalDate).toLocaleDateString(undefined, { weekday:"long", month:"long", day:"numeric", year:"numeric" })}</span>
            {rehearsalVenue && <span className="flex items-center gap-1.5"><MapPin size={13} strokeWidth={1.5} />{rehearsalVenue}</span>}
          </div>
        )}
      </section>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Checklist */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2 flex items-center gap-1.5"><ClipboardList size={11} strokeWidth={1.5} /> MOD.REHEARSAL / CHECKLIST</div>
              <h3 className="font-display text-xl md:text-2xl font-bold text-app tracking-tight leading-none">
                <span className="font-mono tabular-nums">{done.toString().padStart(2, "0")}</span> of <span className="font-mono tabular-nums">{tasks.length.toString().padStart(2, "0")}</span> done
              </h3>
            </div>
            {canEdit && tasks.length === 0 && (
              <button
                onClick={loadTemplate}
                data-testid="rehearsal-load-template"
                className="text-xs text-app underline hover:text-[#000000]"
              >Prefill for {ev.event_type}</button>
            )}
          </div>

          {canEdit && (
            <div className="flex gap-2 mb-4">
              <Input
                data-testid="rehearsal-task-input"
                value={taskInput}
                onChange={(e) => setTaskInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addTask()}
                placeholder="Add a rehearsal to-do"
                className="rounded-sm"
              />
              <Button data-testid="add-rehearsal-task-btn" onClick={addTask} className="rounded-sm bg-app hover:bg-[#000000]">
                <Plus size={14} strokeWidth={1.5} />
              </Button>
            </div>
          )}

          <div className="bevel border-hair rounded-sm divide-y divide-[color:var(--hair)]/60">
            {tasks.length === 0 && (
              <div className="p-8 text-center text-muted font-light text-sm">
                No items yet. {canEdit && "Type above, or use \"Prefill\" to start from a template."}
              </div>
            )}
            {tasks.map(t => (
              <div key={t.id} data-testid={`rehearsal-task-${t.id}`} className="p-3 flex items-start gap-3 group hover:bg-canvas/40">
                <Checkbox
                  data-testid={`rehearsal-task-check-${t.id}`}
                  checked={t.done}
                  onCheckedChange={() => canEdit && toggleTask(t)}
                  disabled={!canEdit}
                  className="mt-0.5 rounded-sm border-hair-strong data-[state=checked]:bg-app data-[state=checked]:border-[#0A0A0A]"
                />
                <div className={`flex-1 text-sm ${t.done ? "line-through text-muted" : "text-app"}`}>{t.title}</div>
                {canEdit && (
                  <button onClick={() => delTask(t.id)} data-testid={`delete-rehearsal-task-${t.id}`} className="text-muted hover:text-[#EF4444] opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={13} strokeWidth={1.5} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Rehearsal timeline */}
        <section>
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2 flex items-center gap-1.5"><Clock size={11} strokeWidth={1.5} /> MOD.REHEARSAL / TIMELINE</div>
              <h3 className="font-display text-xl md:text-2xl font-bold text-app tracking-tight leading-none">
                <span className="font-mono tabular-nums">{blocks.length.toString().padStart(2, "0")}</span> moments
              </h3>
            </div>
            {canEdit && (
              <Dialog open={openBlock} onOpenChange={setOpenBlock}>
                <DialogTrigger asChild>
                  <Button data-testid="add-rehearsal-block-btn" className="rounded-sm bg-app hover:bg-[#000000]">
                    <Plus size={14} strokeWidth={1.5} className="mr-1" /> Add
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-surface rounded-sm max-w-md">
                  <DialogHeader><DialogTitle className="font-serif text-2xl text-app">Add rehearsal moment</DialogTitle></DialogHeader>
                  <div className="space-y-3 py-2">
                    <div className="grid grid-cols-2 gap-3">
                      <F label="Time"><Input data-testid="rehearsal-block-time" type="time" value={blockForm.time} onChange={(e) => setBlockForm({ ...blockForm, time: e.target.value })} /></F>
                      <F label="Duration (min)"><Input type="number" value={blockForm.duration_min} onChange={(e) => setBlockForm({ ...blockForm, duration_min: e.target.value })} /></F>
                    </div>
                    <F label="Title"><Input data-testid="rehearsal-block-title" value={blockForm.title} onChange={(e) => setBlockForm({ ...blockForm, title: e.target.value })} placeholder="e.g. Processional walk-through" /></F>
                    <F label="Details"><Textarea rows={2} className="rounded-sm" value={blockForm.description} onChange={(e) => setBlockForm({ ...blockForm, description: e.target.value })} /></F>
                    <F label="Location"><Input value={blockForm.location} onChange={(e) => setBlockForm({ ...blockForm, location: e.target.value })} placeholder="e.g. Ceremony aisle" /></F>
                  </div>
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpenBlock(false)}>Cancel</Button>
                    <Button data-testid="rehearsal-block-submit" onClick={addBlock} className="rounded-sm bg-app hover:bg-[#000000]">Add</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>

          <div className="bevel border-hair rounded-sm">
            {blocks.length === 0 && (
              <div className="p-8 text-center text-muted font-light text-sm">
                Add a run-of-show for the rehearsal itself.
              </div>
            )}
            {blocks.map(b => (
              <div key={b.id} data-testid={`rehearsal-block-${b.id}`} className="grid grid-cols-[90px_1fr_auto] gap-4 p-4 border-b border-hair/60 last:border-b-0 hover:bg-canvas/40 group">
                <div className="text-right">
                  <div className="font-serif text-xl text-app leading-none">{formatTime(b.time)}</div>
                  <div className="eyebrow mt-1">{b.duration_min}m</div>
                </div>
                <div className="border-l border-hair pl-4 relative">
                  <span className="absolute -left-1.5 top-1.5 w-2.5 h-2.5 rounded-full bg-app" />
                  <div className="text-sm text-app">{b.title}</div>
                  {b.description && <div className="text-xs text-muted font-light mt-0.5">{b.description}</div>}
                  {b.location && <div className="text-xs text-muted flex items-center gap-1 mt-1"><MapPin size={10} strokeWidth={1.5} />{b.location}</div>}
                </div>
                {canEdit && (
                  <button onClick={() => delBlock(b.id)} data-testid={`delete-rehearsal-block-${b.id}`} className="text-muted hover:text-[#EF4444] opacity-0 group-hover:opacity-100 transition-opacity self-start">
                    <Trash2 size={13} strokeWidth={1.5} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function formatTime(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2,"0")} ${ampm}`;
}

function F({ label, children }) {
  return (<div><Label className="eyebrow block mb-2">{label}</Label>{children}</div>);
}
